import React, { useEffect, useState, useRef } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';
import './styles/MapComponent.css';
import styled from 'styled-components';
import config from '../config/config';
import cacheService from '../services/cacheService';

const MapWrapper = styled.div`
  width: 100%;
  height: 100%;
  
  @media (max-width: 768px) {
    height: calc(100vh - 60px); /* Adjust based on your navbar height */
  }
  position: relative;
`;

const CustomOverlay = ({ poi, onPoiClick }) => {
    const map = useMap();
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const element = document.createElement('div');
        element.className = 'custom-overlay home-screen loading';
        
        // Parse image_url if it's a JSON string, otherwise use it as is
        let imageUrl = poi.image_url;
        if (typeof imageUrl === 'string' && imageUrl.startsWith('[')) {
            const parsedImages = JSON.parse(imageUrl);
            imageUrl = parsedImages[0]; // Use the first image
        }

        const imgElement = document.createElement('div');
        
        // Create a temporary image to handle loading
        const tempImage = new Image();
        tempImage.onload = () => {
            imgElement.style.backgroundImage = `url(${imageUrl})`;
            element.classList.remove('loading');
            setIsLoading(false);
        };
        tempImage.onerror = () => {
            // Keep the skeleton state if image fails to load
            console.error('Failed to load image for POI:', poi.id);
        };
        tempImage.src = imageUrl;

        element.appendChild(imgElement);

        const marker = L.marker([poi.lat, poi.lng], {
            icon: L.divIcon({
                className: 'custom-icon',
                html: element,
                iconSize: [78, 78],
                iconAnchor: [39, 78],
            })
        }).addTo(map);

        element.addEventListener('mouseover', () => {
            element.classList.add('hovered');
        });
        element.addEventListener('mouseout', () => {
            element.classList.remove('hovered');
        });
        element.addEventListener('click', () => onPoiClick(poi));

        return () => {
            map.removeLayer(marker);
        };
    }, [map, poi, onPoiClick]);

    return null;
};

const MapBounds = () => {
    const map = useMap();
    
    useEffect(() => {
        const southWest = L.latLng(17.5, -72);
        const northEast = L.latLng(20, -68);
        const bounds = L.latLngBounds(southWest, northEast);
        
        map.setMaxBounds(bounds);
        map.on('drag', () => {
            map.panInsideBounds(bounds, { animate: false });
        });
    }, [map]);

    return null;
};

// Custom TileLayer with cache
const CachedTileLayer = () => {
  const map = useMap();

  useEffect(() => {
    // Enable browser's localStorage caching for tiles
    const originalCreateTile = L.TileLayer.prototype.createTile;
    L.TileLayer.prototype.createTile = function (coords, done) {
      const tile = originalCreateTile.call(this, coords, done);
      const url = tile.src;

      // Try to get cached tile
      const cachedUrl = localStorage.getItem(url);
      if (cachedUrl) {
        tile.src = cachedUrl;
      } else {
        // Cache new tile
        fetch(url)
          .then(response => response.blob())
          .then(blob => {
            const reader = new FileReader();
            reader.onloadend = () => {
              const dataUrl = reader.result;
              try {
                localStorage.setItem(url, dataUrl);
              } catch (e) {
                // If localStorage is full, clear old tiles
                if (e.name === 'QuotaExceededError') {
                  const keys = Object.keys(localStorage);
                  const tilesToRemove = keys.filter(key => key.includes('tile'));
                  tilesToRemove.slice(0, 100).forEach(key => localStorage.removeItem(key));
                }
              }
            };
            reader.readAsDataURL(blob);
          });
      }
      return tile;
    };
  }, []);

  return (
    <TileLayer
      url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
    />
  );
};

const MapComponent = ({ selectedAmenities, allAmenities, onPOIClick }) => {
    const [pois, setPois] = useState([]);
    // const [selectedPOI, setSelectedPOI] = useState(null);

    useEffect(() => {
        const fetchPOIs = async () => {
            try {
                // Use cache service for POIs
                const poisData = await cacheService.getPOIs();
                setPois(poisData);
                console.log('✅ POIs loaded from cache service');
            } catch (error) {
                console.error('Error fetching POIs:', error);
            }
        };

        fetchPOIs();
    }, []);

    const filteredPOIs = selectedAmenities.length === 0
        ? pois
        : pois.filter(poi => 
            poi.amenities && 
            selectedAmenities.some(amenityId => 
                poi.amenities.includes(amenityId)
            )
        );

    const handlePoiClick = (poi) => {
        onPOIClick(poi);
    };

    const center = [18.7357, -70.1627];
    const zoom = 9;

    return (
        <MapWrapper>
            <MapContainer 
                center={center} 
                zoom={zoom} 
                style={{ height: "100%", width: "100%" }}
                minZoom={7}
                maxZoom={18}
                zoomControl={false}
            >
                <CachedTileLayer />
                <MapBounds />
                {filteredPOIs.map(poi => (
                    poi.image_url && <CustomOverlay key={poi.id} poi={poi} onPoiClick={handlePoiClick} />
                ))}
            </MapContainer>
        </MapWrapper>
    );
};

export default MapComponent;
